import React from "react";
import PropTypes from "prop-types";
import Head from "next/head";
import Link from "next/link";
import { Media } from "solar/components/Media";
import Text from "components/shared/stays/Text";
import Title from "components/shared/stays/Title";
import Truncate from "solar/components/Truncate";
import Anchor from "solar/components/Anchor";
import SectionRhythm from "components/shared/stays/SectionRhythm";
import LocationMap from "components/stay/LocationMap";
import styles from "components/stay/Location.module.scss";

export default function Location({
  latitude,
  longitude,
  gettingThere,
  tripTypeDescription,
  regionBreadcrumb,
}) {
  return (
    <>
      {regionBreadcrumb && (
        <Head>
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "http://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  regionBreadcrumb?.map((item, position) => ({
                    "@type": "ListItem",
                    position: position + 1,
                    item: {
                      "@id": `/destinations/${item.slug}`,
                      name: item.name,
                    },
                  })),
                ],
              }),
            }}
          />
        </Head>
      )}
      <div className={styles.component}>
        <Anchor id="location" offset />
        <SectionRhythm>
          <Title>Location</Title>
          {regionBreadcrumb && (
            <Text className={styles.breadcrumb}>
              {regionBreadcrumb.map((obj, idx) => (
                <React.Fragment key={obj.slug}>
                  <Link
                    prefetch={false}
                    href={`/destinations/${obj.slug}`}
                    legacyBehavior
                  >
                    {obj.name}
                  </Link>
                  {idx !== regionBreadcrumb.length - 1 && (
                    <span className={styles.divider}>/</span>
                  )}
                </React.Fragment>
              ))}
            </Text>
          )}
        </SectionRhythm>
        <SectionRhythm>
          <div className={styles.map}>
            {latitude && longitude && (
              <LocationMap
                latitude={latitude}
                longitude={longitude}
                tripTypeDescription={tripTypeDescription}
              />
            )}
          </div>
          <div className={styles.description}>
            <Title size={5}>Getting there</Title>
            <div className={styles.text}>
              <Media lessThan="lg">
                <Text>
                  <Truncate chars={125} more moreText="read more">
                    {gettingThere}
                  </Truncate>
                </Text>
              </Media>
              <Media greaterThan="md">
                <Text>
                  <Truncate chars={250} more moreText="read more">
                    {gettingThere}
                  </Truncate>
                </Text>
              </Media>
            </div>
          </div>
        </SectionRhythm>
      </div>
    </>
  );
}

Location.propTypes = {
  latitude: PropTypes.number.isRequired,
  longitude: PropTypes.number.isRequired,
  gettingThere: PropTypes.string.isRequired,
  tripTypeDescription: PropTypes.string.isRequired,
  regionBreadcrumb: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
    })
  ),
};
